import React from 'react';
import styled from 'styled-components';
import { Tag } from '@decisiv/ui-components';
import SelectMenu from '@decisiv/ui-components/lib/components/SelectMenu';
import AdminPage from 'lib/admin-kit/components/AdminPage';
import AdminTable from 'lib/admin-kit/components/AdminTable';
import SearchBar from '../ai_usage_logs/SearchBar';

const DEALER_BUDGETS_ENDPOINT = '/internal_admin/admin_kit/ai_dealer_budgets';
const AI_SETTINGS_ENDPOINT = '/internal_admin/admin_kit/ai_settings';

const TIER_INTENT = { warn: 'warning', exceeded: 'danger' };
const TIER_LABEL = { warn: 'Warn', exceeded: 'Exceeded' };

const formatUsd = (value) =>
  value == null ? '—' : `$${Number(value).toFixed(2)}`;

const PERIODS = [
  { id: 'week', label: 'Last 7 days' },
  { id: 'month', label: 'Last 30 days' },
  { id: 'all', label: 'All' },
];

// SelectMenu renders its `label` prop as a plain <span> beside the trigger
// button, making the label text a non-interactive dead zone. Same fix as
// AdminFilters: pass label=" " and hide that span via CSS.
const Wrapper = styled.div`
  margin-bottom: 16px;

  & > div > span:first-child {
    display: none;
  }
`;

// Full-page navigation with a `?period=` query param — the controller
// re-renders fresh props, so no client-side fetch/state is needed here.
// `q`/`searchField` (if present) are preserved across period switches, same
// convention as ai_usage_logs/PeriodSelector.
const GlobalExceededHistoryPeriodSelector = ({
  period,
  basePath,
  q = '',
  searchField = '',
}) => (
  <Wrapper>
    <SelectMenu
      label=" "
      options={PERIODS}
      selected={period}
      size="small"
      onChange={(id) => {
        const params = new URLSearchParams({ period: id });
        if (q) {
          params.set('q', q);
          if (searchField) params.set('search_field', searchField);
        }
        window.location.href = `${basePath}?${params.toString()}`;
      }}
    />
  </Wrapper>
);

const globalExceededHistoryColumns = [
  {
    name: 'dealer_name',
    title: 'Dealer',
    DataCell: ({ rowData }) => (
      <a
        href={`${DEALER_BUDGETS_ENDPOINT}/exceeded_history/${rowData.dealer_id}`}
      >
        {rowData.dealer_name ?? rowData.dealer_id}
      </a>
    ),
  },
  {
    name: 'day',
    title: 'Date',
    DataCell: ({ rowData }) => {
      if (!rowData.day) return '—';
      const date = new Date(rowData.day);
      return date.toLocaleDateString();
    },
  },
  {
    name: 'tier',
    title: 'Tier',
    DataCell: ({ rowData }) => (
      <Tag
        intent={TIER_INTENT[rowData.tier] ?? 'neutral'}
        text={TIER_LABEL[rowData.tier] ?? rowData.tier}
      />
    ),
  },
  {
    name: 'triggered_at',
    title: 'Triggered At',
    DataCell: ({ rowData }) => {
      if (!rowData.triggered_at) return '—';
      const date = new Date(rowData.triggered_at);
      return date.toLocaleTimeString();
    },
  },
  {
    name: 'threshold_usd',
    title: 'Threshold at Trigger',
    DataCell: ({ rowData }) => formatUsd(rowData.threshold_usd),
  },
];

const GlobalExceededHistory = ({
  rows = [],
  period = 'month',
  q = '',
  searchField = 'dealer_id',
}) => {
  const basePath = `${DEALER_BUDGETS_ENDPOINT}/exceeded_history`;

  return (
    <AdminPage
      title="Budget History — All Dealers"
      breadcrumbs={[
        { text: 'AI Budget Settings', href: AI_SETTINGS_ENDPOINT },
        { text: 'Budget History — All Dealers' },
      ]}
    >
      <SearchBar
        initialField={searchField}
        initialValue={q}
        onSearch={(field, text) => {
          const params = new URLSearchParams({ period, search_field: field });
          if (text) params.set('q', text);
          window.location.href = `${basePath}?${params.toString()}`;
        }}
      />
      <GlobalExceededHistoryPeriodSelector
        period={period}
        basePath={basePath}
        q={q}
        searchField={searchField}
      />

      <div>
        <AdminTable
          columns={globalExceededHistoryColumns}
          data={rows}
          getRowKey={(r) =>
            `${r.dealer_id}-${r.day}-${r.tier}-${r.threshold_usd}`
          }
          emptyText="No dealers recorded exceeding their budget in this period."
        />
      </div>
    </AdminPage>
  );
};

export default GlobalExceededHistory;
